import { type Facility } from '../domain/Facility'
import { type FacilityContact } from '../domain/FacilityContact'
import { type FacilityHours } from '../domain/FacilityHours'
import { type FacilityPerformance } from '../domain/FacilityPerformance'
import { type FacilityReport } from '../domain/FacilityReport'

export type RawFacility = {
  uuid: string
  company_name: string
  address: string
  zipcode: string
  city: string
  state: string
  country: string
  score: number | null
}

export function mapApiToFacility(rawFacility: RawFacility): Facility {
  if (!rawFacility) throw new Error('rawFacility is required')

  return {
    uuid: rawFacility.uuid,
    companyName: rawFacility.company_name,
    address: rawFacility.address,
    zipcode: rawFacility.zipcode,
    city: rawFacility.city,
    state: rawFacility.state,
    country: rawFacility.country,
    score: rawFacility.score ?? undefined,
  }
}

export type RawFacilityPerformance = {
  avg_time_spent: {
    pickup: number | null
    delivery: number | null
  }
  detention_rate: number | null
  score: number | null
}

export function mapApiToFacilityPerformance(
  rawFacilityPerformance: RawFacilityPerformance
): FacilityPerformance {
  if (!rawFacilityPerformance) throw new Error('rawFacilityPerformance is required')

  const { avg_time_spent, detention_rate, score } = rawFacilityPerformance

  return {
    pickupDwellTime: avg_time_spent.pickup ?? undefined,
    deliveryDwellTime: avg_time_spent.delivery ?? undefined,
    detentionRate: detention_rate ?? undefined,
    score: score ?? undefined,
  }
}

export type RawFacilityHours = {
  uuid: string
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
  holiday: boolean
  mode: 'appt' | 'fcfs'
  opens: string
  closes: string
  service: {
    equipment_types: Array<string>
    load_methods: Array<string>
    stop_types: Array<'PU' | 'DEL'>
  }
}

export function mapApiToFacilityHours(
  rawFacilityHours: RawFacilityHours
): FacilityHours | undefined {
  if (!rawFacilityHours) return

  return {
    uuid: rawFacilityHours.uuid,
    mode: rawFacilityHours.mode,
    opens: rawFacilityHours.opens,
    closes: rawFacilityHours.closes,
    service: {
      equipmentTypes: rawFacilityHours.service.equipment_types,
      loadMethods: rawFacilityHours.service.load_methods,
      stopTypes: rawFacilityHours.service.stop_types,
    },
    weekdays: {
      monday: rawFacilityHours.monday,
      tuesday: rawFacilityHours.tuesday,
      wednesday: rawFacilityHours.wednesday,
      thursday: rawFacilityHours.thursday,
      friday: rawFacilityHours.friday,
      saturday: rawFacilityHours.saturday,
      sunday: rawFacilityHours.sunday,
      holiday: rawFacilityHours.holiday,
    },
  }
}

export type RawFacilityContact = {
  uuid: string
  is_preferable: boolean
  is_shipper: boolean
  is_receiver: boolean
  name: string
  email: string
  phone_number?: string
  phone_number_extension?: string | null
  owner_type?: string | null
  owner_uuid?: string | null
  tms_link?: string | null
}

export function mapApiToFacilityContact(rawFacilityContact: RawFacilityContact): FacilityContact {
  return {
    uuid: rawFacilityContact.uuid,
    isPreferable: rawFacilityContact.is_preferable,
    isShipper: rawFacilityContact.is_shipper,
    isReceiver: rawFacilityContact.is_receiver,
    name: rawFacilityContact.name,
    email: rawFacilityContact.email,
    phoneNumber: rawFacilityContact.phone_number,
    phoneNumberExtension: rawFacilityContact.phone_number_extension ?? undefined,
    ownerType: rawFacilityContact.owner_type ?? undefined,
    ownerUuid: rawFacilityContact.owner_uuid ?? undefined,
    tmsLink: rawFacilityContact.tms_link ?? undefined,
  }
}

type rawTags = Partial<{
  'waiting-time': number
  'finding-entrance': number
  'appointment-flexibility': number
  'turning-room': number
  amenities: number
  'staff-politeness': number
  other: number
}>

export type RawFacilityReport = {
  count: number
  score: number | null
  negative_tags: rawTags
  positive_tags: rawTags
  score_counts: Partial<{
    '1': number
    '2': number
    '3': number
    '4': number
    '5': number
  }>
}

export function mapApiToFacilityReport(rawFacilityReport: RawFacilityReport): FacilityReport {
  if (!rawFacilityReport) throw new Error('RawFacilityReport is required')

  return {
    count: rawFacilityReport.count,
    score: rawFacilityReport.score ?? 0,
    scoreCounts: rawFacilityReport.score_counts,
    negativeTags: rawFacilityReport.negative_tags,
    positiveTags: rawFacilityReport.positive_tags,
  }
}
